import { call, put } from 'redux-saga/effects';

import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { setOrDeleteLabel } from '~src/catalog/restApi/labels/Labels';
import { isWarnable, LabelContentItem } from '~src/catalog/restApi/labels/LabelTypes';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { delayPromise } from '~src/commonInfra/utils/Delay';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { fetchContent, reloadSpaceIfUpdatingCurrentSpace } from '~src/spacesPlatform/sagas/content/RefreshContentSagas';
import { replaceDatasourceIdWithConnectionIdForMixedContent } from '~src/spacesPlatform/sagas/SpacesSagaHelpers';

import { requestFailed } from '../../../bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '../../../codegen/Localize';
import { IContentInfo } from '../../../spacesPlatform/models/IContentInfo';
import { SetDataQualityAction } from '../../actions/dataQuality/SetDataQualityAction';
import { DataQualityDialogBodyProps, defaultInitialState, getDataQualityActionDialogOptions, WarningDialogState } from '../../components/dataQuality/DataQualityDialog';
import { CERTIFIED_INDICATOR_TYPE, stringToDataQualityType, userEditableDataQualityTypes } from '../../components/dataQuality/DataQualityType';
import { DataQualityWarningActions } from '../../contentActions/DataQualityWarningActions';


export function* handleSetDataQuality(action: SetDataQualityAction) {
  try {
    let mutatableContentCopy: IContentInfo[] = [];
    mutatableContentCopy = yield call(replaceDatasourceIdWithConnectionIdForMixedContent, [action.item]);

    const first = mutatableContentCopy[0] ?? undefined;
    const item: IContentInfo = yield fetchContent(first);
    const fn = FeatureFlags.columnDQWsEnabled ? openDataQualityDialog : openDataQualityDialogOld;

    yield call(fn, { type: action.type, item });

    const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, [action.item]);
    if (!maybeReloaded) {
      yield put(requestSpaceContents());
    }

  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function openDataQualityDialog(action: SetDataQualityAction): PromiseLike<unknown> {
  let { luid: contentId, name: contentName, contentType } = action.item;

  if(contentType === 'tableWithCrass') {
    contentType = 'table';
  }
  if(contentType === 'databaseWithCrass') {
    contentType = 'database';
  }
  if (!isWarnable(contentType)) {
    throw new Error(`${action.item.contentType} is not warnable`);
  }

  const contentItem: LabelContentItem = { contentId, contentName, contentType };

  return openDataQualityDialogCatalog({ contentItem, action });
}

export function openDataQualityDialogCatalog({ contentItem, action, onSubmit }: {
  readonly contentItem: LabelContentItem;
  readonly action?: SetDataQualityAction;
  readonly onSubmit?: () => void;
}): Promise<unknown> {

  const doAction: (formData: WarningDialogState) => Promise<unknown> = ({ luid, isActive, isSevere, message, type }) => {
    return setOrDeleteLabel({
      luid: luid,
      category: 'warning',
      contentItems: [contentItem],
      labelContent: {
        active: isActive,
        elevated: isSevere,
        message,
        value: type
      }
    }).then(async _ => {
      ToasterService.instance.pop({
        type: ToastType.Info,
        text: Messages.dataQuality_updateSuccess({ CONTENT_NAME: contentItem.contentName }),
      });
      if (contentItem.contentType === 'column') {
        /**
         * Columns are a special case and should wait an additional 3 seconds for a total of 5 which is the current p95 of graph ingestion time
         * Until we can make architectural improvements to the Labels UI to optimistically update our state or the graph's p95 improves
         */
        await delayPromise(3000);
      }
      if (onSubmit) {
        ToasterService.instance.markToastsAsDoNotDismissOnLocationChange();
        onSubmit();
      }
    }).catch(_ => ToasterService.instance.pop({
      type: ToastType.Error,
      text: Messages.dataQuality_setError({ CONTENT_NAME: contentItem.contentName })
    }));
  };

  const initialBodyProps: DataQualityDialogBodyProps = { contentItem };
  const modalInstance = openReactActionDialog(getDataQualityActionDialogOptions({
    initialBodyProps,
    initialFormData: defaultInitialState,
    doAction,
  }));

  return modalInstance.result;
}

export function openDataQualityDialogOld(action: SetDataQualityAction): PromiseLike<unknown> {

  const indicator = action.item.dataQualityIndicators.filter(t => t.type !== CERTIFIED_INDICATOR_TYPE).find(
    dqi => userEditableDataQualityTypes.includes(stringToDataQualityType(dqi.type))
  );
  const isUpdate = !!indicator && !!indicator.id;

  let contentItemId = action.item.id;
  if (action.item.contentType === 'table' || action.item.contentType === 'tableWithCrass' || action.item.contentType === 'database' || action.item.contentType === 'databaseWithCrass') {
    contentItemId = action.item.vizportalId;
  }

  const addDataQualityWarning = (state: WarningDialogState) => {
    return DataQualityWarningActions.addDataQualityWarning({
      contentName: action.item.name,
      contentId: contentItemId,
      contentType: action.item.contentType,
      ...state
    });
  };

  const updateDataQualityWarning = (state: WarningDialogState) => {
    if (state.isActive || state.message.length > 0) {
      return DataQualityWarningActions.updateDataQualityWarning({
        contentName: action.item.name,
        id: indicator.id,
        ...state
      });
    } else {
      return DataQualityWarningActions.deleteDataQualityWarning({
        contentName: action.item.name,
        id: indicator.id
      });
    }
  };

  const setDataQualityWarning = isUpdate ? updateDataQualityWarning : addDataQualityWarning;

  if (!isWarnable(action.item.contentType)) {
    throw new Error(`${action.item.contentType} is not warnable`);
  }

  const initialBodyProps: DataQualityDialogBodyProps = {
    contentItem: {
      contentId: action.item.luid,
      contentName: action.item.name,
      contentType: action.item.contentType,
    }
  };

  const modalInstance = openReactActionDialog(getDataQualityActionDialogOptions({
    initialBodyProps,
    initialFormData: isUpdate ? { ...indicator } : defaultInitialState,
    doAction: setDataQualityWarning
  }));
  return modalInstance.result;
}
